import React, {useEffect, useState} from 'react';
import {Dropdown, Button} from 'antd';
import {DownOutlined, FolderOutlined, FileTextOutlined} from '@ant-design/icons';
import {useHistory} from 'react-router-dom';
import './standards-menu.scss';

// Cascading "Standards" dropdown in the portal header. Source of truth is
// editor's /library endpoint — same tree the editor renders in its own
// workgroup-list page, no duplicated workgroup→spec metadata.
//
// /library returns:
//   [{title, value, children: [{title, value, children: []}, ...]}, ...]
//   - top level = workgroup; value = workgroup slug
//   - children   = specs in that workgroup; value = `${workgroup}-${specKey}`
//
// Click a spec → history.push(`/v1/standards/${wg}/${key}/latest/overview`).
// Workgroups with no specs are omitted (empty submenu would just confuse).

function StandardsMenu() {
    const [tree, setTree] = useState(null);
    const [error, setError] = useState(null);
    const history = useHistory();

    useEffect(() => {
        let cancelled = false;
        fetch('/library')
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((d) => {
                if (!cancelled) setTree(Array.isArray(d) ? d : []);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    function navigateToSpec(workgroup, key) {
        history.push(`/v1/standards/${workgroup}/${key}/latest/overview`);
    }

    function buildItems(workgroups) {
        if (error) return [{key: 'error', label: `Error: ${error}`, disabled: true}];
        if (!workgroups || workgroups.length === 0) {
            return [{key: 'empty', label: 'Loading…', disabled: true}];
        }

        return workgroups
            .filter((wg) => wg.children && wg.children.length > 0)
            .map((wg) => ({
                key: wg.value,
                icon: <FolderOutlined />,
                label: wg.title,
                children: wg.children.map((spec) => {
                    // editor returns spec.value as `${workgroup}-${key}`;
                    // strip the workgroup prefix to recover the bare key.
                    const prefix = `${wg.value}-`;
                    const specKey = spec.value.startsWith(prefix)
                        ? spec.value.slice(prefix.length)
                        : spec.value;
                    return {
                        key: spec.value,
                        icon: <FileTextOutlined />,
                        label: spec.title,
                        onClick: () => navigateToSpec(wg.value, specKey),
                    };
                }),
            }));
    }

    return (
        <Dropdown
            menu={{items: buildItems(tree)}}
            trigger={['click']}
            placement="bottomLeft"
        >
            <Button type="text" className="portal-standards-menu__btn">
                Standards <DownOutlined />
            </Button>
        </Dropdown>
    );
}

export default StandardsMenu;
